import React from 'react';
import { useIntl } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { Cover } from '../../components/primitives/Cover';
import type { BrowseArtistItem } from '../hooks/useBrowseSearch';
import { formatChange, formatCount, formatReach } from '../browseFormat';
import { browseMessages as messages } from '../browseMessages';
import { BrowseCostChip } from './BrowseCostChip';
import { BrowseFollowButton } from './BrowseFollowButton';
import { BrowseOpenLink } from './BrowseOpenLink';
import { changeToneClass } from './changeToneClass';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseRows.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseArtistRowProps {
  artist: BrowseArtistItem;
}

/** One artist row (design a1 Artists panel; c1 on a phone). */
const BrowseArtistRowComponent: React.FC<BrowseArtistRowProps> = ({ artist }) => {
  const intl = useIntl();
  const genres = artist.genres.slice(0, 3).join(' · ');
  const followed = artist.state === 'followed';

  return (
    <div className={clsx(styles.resultRow, styles.artistGrid)} data-testid="browse-artist-row">
      <Cover src={artist.imageUrl} label={artist.name} size={44} radius="50%" />
      <BrowseOpenLink kind="artist" id={artist.id} state={artist.state} className={styles.nameCell}>
        <span className={styles.nameMain}>{artist.name}</span>
        {genres !== '' && <span className={styles.nameSub}>{genres}</span>}
        <span className={clsx(styles.nameSub, styles.phoneFigure, styles.showOnPhone)}>
          {intl.formatMessage(messages.reachShort, { value: formatReach(intl, artist.reach) })}
        </span>
        <BrowseCostChip state={artist.state} className={styles.showOnPhone} />
      </BrowseOpenLink>
      <span className={clsx(styles.num, styles.hideOnPhone)}>{formatCount(intl, artist.followers)}</span>
      <span className={clsx(styles.num, styles.hideOnPhone)}>{formatReach(intl, artist.reach)}</span>
      <span className={clsx(styles.num, styles.hideOnPhone, changeToneClass(artist.change7d))}>
        {formatChange(intl, artist.change7d)}
      </span>
      <BrowseCostChip state={artist.state} className={styles.hideOnPhone} />
      <span className={clsx(styles.hideOnPhone, styles.right)}>
        <BrowseFollowButton kind="artist" id={artist.id} name={artist.name} followed={followed} />
      </span>
      <span className={clsx(styles.showOnPhone, styles.right)}>
        <BrowseFollowButton kind="artist" id={artist.id} name={artist.name} followed={followed} iconOnly />
      </span>
    </div>
  );
};

export const BrowseArtistRow = TypedMemo(BrowseArtistRowComponent);
